import {theme} from '@styles/theme';
import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import {DefaultTheme} from 'styled-components';
import {
  Option,
  OptionText,
  SelectItem,
  SelectList,
  SelectWrapper,
  Title,
} from './OptionButton.styles';
import {
  EventSchedule,
  CalendarAddAlt,
  CalendarHeatMap,
  Checkmark,
} from '@carbon/icons-react';
import {useOnClickOutside} from '@hooks/useOnClickOutside';
import Tooltip from '@atoms/Tooltip';

export interface OptionButtonProps {
  type?: 'calendar' | 'default';
  value: string | number;
  onChange?: (e: any) => void;
  options?: Array<{title: string; value: string}>;
  inputDisabled?: boolean;
  readableWhenDisabled?: boolean;
  disabled?: string;
  textColor?: string;
  menuWidth?: string;
  wrap?: boolean;
  optimizeRendering?: boolean;
  withoutIcon?: boolean;
  fixedWidth?: number;
  dropdownContent?: React.ReactNode;
  buttonTitle?: string;
  dropdownAlign?: 'left' | 'right';
  plannedToday?: boolean;
  iconVariant?: 'plan' | 'date';
  listPositionOverride?: 'top' | 'bottom';
  variant?: 'default' | 'filledBlue';
  withoutSeparator?: boolean;
}

export interface StyledOptionButtonProps {
  theme: DefaultTheme;
  focus?: boolean;
  textColor?: string;
  inputDisabled?: boolean;
  readableWhenDisabled?: boolean;
  rounded?: boolean;
  wrap?: boolean;
  fixedWidth?: number;
  borderColor?: string;
  backgroundColor?: string;
  showSeparator?: boolean;
}

const Select: FC<OptionButtonProps> = ({
  type = 'default',
  value,
  onChange,
  options,
  inputDisabled,
  readableWhenDisabled,
  disabled,
  textColor = `${theme.colors.basic00}`,
  menuWidth = 'inherit',
  wrap,
  optimizeRendering,
  withoutIcon = false,
  fixedWidth,
  dropdownContent,
  buttonTitle,
  dropdownAlign = 'left',
  plannedToday,
  iconVariant = 'date',
  listPositionOverride,
  variant = 'default',
  withoutSeparator,
}) => {
  const [isFocus, setIsFocus] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [currentValue, setCurrentValue] = useState(value ?? options?.[0]?.value);
  const [listPosition, setListPosition] = useState<'top' | 'bottom'>('top');
  const [showTooltip, setShowTooltip] = useState(false);

  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const selectListRef = useRef<HTMLDivElement>(null);
  const selectWrapperRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(selectWrapperRef, () => {
    setIsOpen(false);
    setIsFocus(false);
  });

  useEffect(() => {
    if (!selectListRef.current) return;
    const active = selectListRef.current.querySelector('.active');
    if (active && 'scrollIntoView' in active) {
      (active as HTMLElement).scrollIntoView({block: 'nearest'});
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    if (!options?.length) return;
    const exists = options.some(o => o.value === currentValue);
    if (!exists) {
      setCurrentValue(options[0].value);
    }
  }, [options]);

  const handleValueChange = (value: string) => {
    setCurrentValue(value);
  };

  const handleChange = (value: string) => {
    handleValueChange(value);

    if (onChange) onChange(value);

    setIsOpen(false);
    setIsFocus(false);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    filteredPersons: Array<{title: string; value: string}>,
  ) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(
        highlightedIndex === 0 ? filteredPersons.length - 1 : highlightedIndex - 1,
      );
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(
        highlightedIndex === filteredPersons.length - 1 ? 0 : highlightedIndex + 1,
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      handleChange(filteredPersons[highlightedIndex].value);
    }
  };

  const displayTitle = useMemo(() => {
    if (!options?.length) return buttonTitle ?? '';
    const current = options.find(option => option.value === currentValue);
    return current ? current.title : options[0].title;
  }, [options, currentValue, buttonTitle]);

  function adjustDropdownPosition() {
    if (listPositionOverride) {
      setListPosition(listPositionOverride);
      return;
    }
    const rect = selectWrapperRef?.current?.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect!.bottom;
    // With current styles mapping, 'bottom' opens dropdown below, 'top' opens above
    setListPosition(spaceBelow < 300 ? 'top' : 'bottom');
  }

  const closeDropdown = () => {
    setIsOpen(false);
    setIsFocus(false);
  };

  const isFilledBlue = variant === 'filledBlue';
  const resolvedTextColor = isFilledBlue ? theme.colors.basic08 : textColor;
  const resolvedFixedWidth = fixedWidth ?? (isFilledBlue ? 232 : undefined);
  const resolvedWithoutIcon = withoutIcon || isFilledBlue;
  const resolvedBorderColor = plannedToday
    ? theme.colors.signalOrange00
    : isFilledBlue
      ? theme.colors.accent00
      : undefined;
  const resolvedBackgroundColor = plannedToday
    ? theme.colors.signalOrange01
    : isFilledBlue
      ? theme.colors.accent00
      : undefined;

  const renderIcon = () => {
    if (resolvedWithoutIcon) return null;
    if (iconVariant === 'plan') {
      return <CalendarAddAlt size="16" />;
    }
    if (plannedToday) {
      return <CalendarHeatMap size="16" color={resolvedBorderColor} />;
    }
    return <EventSchedule size="16" />;
  };

  return (
    <>
      <SelectWrapper
        onClick={e => {
          e.stopPropagation();
          if (!inputDisabled) {
            adjustDropdownPosition();
            setIsOpen(!isOpen);
            setIsFocus(!isFocus);
          }
        }}
        focus={isFocus}
        inputDisabled={inputDisabled}
        onKeyDown={e => handleKeyDown(e, options ?? [])}
        tabIndex={0}
        ref={selectWrapperRef}
        fixedWidth={resolvedFixedWidth}
        borderColor={resolvedBorderColor}
        backgroundColor={resolvedBackgroundColor}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}>
        <SelectItem
          onBlur={() => setIsFocus(false)}
          textColor={resolvedTextColor}
          inputDisabled={inputDisabled}
          readableWhenDisabled={readableWhenDisabled}
          wrap={wrap}
          open={isOpen}
          showSeparator={!(withoutSeparator || isFilledBlue)}>
          {renderIcon()}
          {displayTitle}
        </SelectItem>
        {(!optimizeRendering || isOpen) && (
          <SelectList
            isOpen={isOpen}
            id="selectList"
            ref={selectListRef}
            position={listPosition}
            menuWidth={menuWidth}
            align={dropdownAlign}
            type={type}
            scrollable={dropdownContent ? false : true}
            onClick={e => e.stopPropagation()}>
            {dropdownContent ? (
              <>
                {React.isValidElement(dropdownContent)
                  ? React.cloneElement(dropdownContent as React.ReactElement<any>, {
                      onCloseDropdown: closeDropdown,
                    })
                  : dropdownContent}
              </>
            ) : (
              (options ?? []).map((option, index) => {
                return (
                  <Option
                    onClick={e => {
                      e.stopPropagation();
                      handleChange(option.value);
                      setHighlightedIndex(index);
                      setIsOpen(false);
                      setIsFocus(false);
                    }}
                    onMouseMove={() => {
                      setHighlightedIndex(index);
                    }}
                    className={index === highlightedIndex ? 'active' : ''}
                    highlighted={index === highlightedIndex}
                    id={option.value}
                    choosed={option.value === currentValue}
                    key={`${option.value}-${index}`}
                    disabled={option.value === disabled}
                    deleteOption={option.value === 'delete'}>
                    <OptionText>
                      {option.value === currentValue && (
                        <Checkmark width={9} fill={theme.colors.basic02} />
                      )}
                      <Title>{option.title}</Title>
                    </OptionText>
                  </Option>
                );
              })
            )}
          </SelectList>
        )}
        {!!plannedToday && (
          <Tooltip
            isVisible={showTooltip}
            text={`На сегодняшнюю дату есть план покрытия`}
          />
        )}
      </SelectWrapper>
    </>
  );
};

export default Select;
